import * as React from "react";
import { cn } from "@/lib/utils";
import { SectionLabel } from "./GradientText";

interface Logo {
  id: string;
  name: string;
  image: string;
}

interface IntegrationLogosProps {
  title?: string;
  logos: Logo[];
}

export function IntegrationLogos({ title, logos }: IntegrationLogosProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        {title && (
          <div
            className={cn(
              "text-center mb-12 lg:mb-16",
              "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            )}
          >
            <SectionLabel>{title}</SectionLabel>
          </div>
        )}

        {/* Logo grid */}
        <div
          className={cn(
            "flex flex-wrap items-center justify-center gap-8 lg:gap-12",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
          style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
        >
          {logos.map((logo, index) => (
            <div
              key={logo.id}
              className={cn(
                "relative group",
                "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              )}
              style={{
                transitionDelay: isVisible ? `${200 + index * 80}ms` : "0ms",
              }}
            >
              <div
                className={cn(
                  "h-12 w-24 lg:h-14 lg:w-28 flex items-center justify-center",
                  "grayscale opacity-50",
                  "hover:grayscale-0 hover:opacity-100",
                  "transition-all duration-300"
                )}
              >
                <img
                  src={logo.image}
                  alt={logo.name}
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.parentElement!.innerHTML = `
                      <span class="text-muted-foreground text-sm font-medium">${logo.name}</span>
                    `;
                  }}
                />
              </div>

              {/* Tooltip */}
              <div
                className={cn(
                  "absolute -bottom-8 left-1/2 -translate-x-1/2",
                  "px-2 py-1 rounded bg-foreground border border-border",
                  "text-xs text-background whitespace-nowrap",
                  "opacity-0 group-hover:opacity-100",
                  "transition-opacity duration-200",
                  "pointer-events-none"
                )}
              >
                {logo.name}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
